/**
 * DRBFM API Service — pure response mappers and request builders
 *
 * snake_case (API) <-> camelCase (UI) transforms. No network access here.
 */

import {
  DRBFMSession,
  SessionResultsResponse,
  KnowledgeReference,
  DrawingEvidence,
  ReviewQuestion,
  DesignChange,
  FailureMode,
  Countermeasure,
  DRBFMCase,
  DRBFMCaseDesignChange,
  DRBFMCasePrecheckItem,
  DRBFMStageRun,
  DRBFMWorkflowArtifact,
  DRBFMWorkflowResponse,
  DRBFMReadiness,
  UsedPrecheckAnswer,
  FunctionPartMatrix,
} from '../types';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const firstPresent = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return undefined;
};

export const mapOptionalNumber = (...values: unknown[]): number | undefined => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return undefined;
};

const mapKnowledgeMetadata = (kr: any): Record<string, unknown> | undefined => {
  const metadata = kr.metadata ?? kr.evidence_metadata;
  return metadata && typeof metadata === 'object' && !Array.isArray(metadata)
    ? metadata
    : undefined;
};

const mapDrawingEvidence = (value: any): DrawingEvidence | undefined => {
  if (!value || typeof value !== 'object' || !value.bbox) {
    return undefined;
  }

  const source = ['document_image', 'pdf_page', 'llm_inferred'].includes(value.source)
    ? value.source
    : 'llm_inferred';
  const bboxUnit = ['normalized', 'pixel', 'pdf_point'].includes(value.bbox.unit)
    ? value.bbox.unit
    : 'normalized';

  return {
    documentId: firstPresent(value.document_id, value.documentId) || '',
    imageId: firstPresent(value.image_id, value.imageId),
    pageNumber: mapOptionalNumber(value.page_number, value.pageNumber),
    label: firstPresent(value.label, value.title) || '',
    confidence: Number(value.confidence ?? 0),
    source,
    bbox: {
      x: Number(value.bbox.x),
      y: Number(value.bbox.y),
      width: Number(value.bbox.width),
      height: Number(value.bbox.height),
      unit: bboxUnit,
    },
    reasoning: firstPresent(value.reasoning, value.description),
  };
};

const mapReviewQuestions = (value: any): ReviewQuestion[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter((item) => item && typeof item.question === 'string' && item.question.trim())
    .map((item, index) => ({
      id: firstPresent(item.id) || `question-${index + 1}`,
      question: item.question,
      reason: firstPresent(item.reason),
      required: item.required !== false,
    }));
};

const isDisplayableKnowledgeTitle = (value?: string): boolean => {
  if (!value) return false;
  const normalized = value.trim();
  return normalized.length > 0
    && normalized.toLowerCase() !== 'unknown'
    && !UUID_PATTERN.test(normalized);
};

export const mapKnowledgeReference = (kr: any): KnowledgeReference => {
  const documentId = firstPresent(kr.document_id, kr.documentId, kr.id) || '';
  const rawTitle = firstPresent(kr.document_title, kr.documentTitle, kr.title);
  const fileName = firstPresent(kr.file_name, kr.fileName, kr.filename);
  const fallbackTitle = fileName || rawTitle || documentId;

  return {
    documentId,
    title: isDisplayableKnowledgeTitle(rawTitle) ? rawTitle! : fallbackTitle,
    fileName,
    snippet: firstPresent(kr.excerpt, kr.snippet, kr.content_preview, kr.contentPreview),
    relevanceScore: kr.relevance_score ?? kr.relevanceScore ?? 0,
    category: kr.category,
    pageNumber: mapOptionalNumber(kr.page_number, kr.pageNumber),
    imageId: firstPresent(kr.image_id, kr.imageId),
    metadata: mapKnowledgeMetadata(kr),
  };
};

const firstArray = (...values: unknown[]): unknown[] | undefined => (
  values.find(Array.isArray) as unknown[] | undefined
);

const mapUsedPrecheckAnswers = (
  ...values: unknown[]
): UsedPrecheckAnswer[] => {
  const value = firstArray(...values);
  return value ? value as UsedPrecheckAnswer[] : [];
};

const mapUnknownAssumptions = (...values: unknown[]): string[] => {
  const value = firstArray(...values);
  return value ? value.filter((item): item is string => typeof item === 'string') : [];
};

export const mapSession = (session: any): DRBFMSession => ({
  id: session.id,
  tenantId: session.tenant_id ?? session.tenantId,
  userId: session.user_id ?? session.userId,
  itemName: session.item_name ?? session.itemName,
  title: session.title,
  specOldDocumentId: session.spec_old_document_id ?? session.specOldDocumentId,
  specNewDocumentId: session.spec_new_document_id ?? session.specNewDocumentId,
  workflowPhase: session.workflow_phase ?? session.workflowPhase,
  inputPattern: session.input_pattern ?? session.inputPattern,
  changeGranularity: session.change_granularity ?? session.changeGranularity,
  workflowMetadata: session.workflow_metadata ?? session.workflowMetadata ?? {},
  status: session.status,
  currentFlow: session.current_flow ?? session.currentFlow,
  flowProgress: session.flow_progress ?? session.flowProgress ?? {},
  createdAt: session.created_at ?? session.createdAt,
  updatedAt: session.updated_at ?? session.updatedAt,
  completedAt: session.completed_at ?? session.completedAt,
  htmlExportPath: session.html_export_path ?? session.htmlExportPath,
});

export const mapSessionDesignChange = (dc: any): DesignChange => ({
  id: dc.id,
  sessionId: dc.session_id ?? dc.sessionId,
  category: dc.category,
  itemName: dc.item_name ?? dc.itemName,
  oldSpecification: dc.old_specification ?? dc.oldSpecification,
  newSpecification: dc.new_specification ?? dc.newSpecification,
  changeDescription: dc.change_description ?? dc.changeDescription,
  impactAreas: dc.impact_areas ?? dc.impactAreas ?? [],
  sourceArtifactId: dc.source_artifact_id ?? dc.sourceArtifactId ?? null,
  reviewStatus: dc.review_status ?? dc.reviewStatus,
  granularity: dc.granularity ?? null,
  confidence: dc.confidence ?? null,
  reviewerNote: dc.reviewer_note ?? dc.reviewerNote ?? null,
  changeMetadata: dc.change_metadata ?? dc.changeMetadata ?? {},
  canonicalKey: dc.canonical_key ?? dc.canonicalKey ?? null,
  generationEligible: dc.generation_eligible ?? dc.generationEligible ?? false,
  sourceEvidence: dc.source_evidence ?? dc.sourceEvidence ?? [],
  reviewedBy: dc.reviewed_by ?? dc.reviewedBy ?? null,
  reviewedAt: dc.reviewed_at ?? dc.reviewedAt ?? null,
  createdAt: dc.created_at ?? dc.createdAt,
});

export const mapWorkflowArtifact = (artifact: any): DRBFMWorkflowArtifact => ({
  id: artifact.id,
  tenantId: artifact.tenant_id ?? artifact.tenantId,
  sessionId: artifact.session_id ?? artifact.sessionId,
  artifactType: artifact.artifact_type ?? artifact.artifactType,
  sourceType: artifact.source_type ?? artifact.sourceType,
  status: artifact.status,
  documentId: artifact.document_id ?? artifact.documentId ?? null,
  cadModelId: artifact.cad_model_id ?? artifact.cadModelId ?? null,
  title: artifact.title ?? null,
  payload: artifact.payload ?? {},
  createdBy: artifact.created_by ?? artifact.createdBy ?? null,
  createdAt: artifact.created_at ?? artifact.createdAt ?? null,
  updatedAt: artifact.updated_at ?? artifact.updatedAt ?? null,
});

export const mapStageRun = (run: any): DRBFMStageRun => ({
  id: run.id,
  tenantId: run.tenant_id ?? run.tenantId,
  sessionId: run.session_id ?? run.sessionId,
  stage: run.stage,
  status: run.status,
  inputArtifactIds: run.input_artifact_ids ?? run.inputArtifactIds ?? [],
  outputArtifactIds: run.output_artifact_ids ?? run.outputArtifactIds ?? [],
  startedAt: run.started_at ?? run.startedAt ?? null,
  completedAt: run.completed_at ?? run.completedAt ?? null,
  errorMessage: run.error_message ?? run.errorMessage ?? null,
  runMetadata: run.run_metadata ?? run.runMetadata ?? {},
  createdBy: run.created_by ?? run.createdBy ?? null,
  createdAt: run.created_at ?? run.createdAt ?? null,
});

export const mapWorkflowResponse = (response: any): DRBFMWorkflowResponse => ({
  session: mapSession(response.session ?? {}),
  artifacts: (response.artifacts ?? []).map(mapWorkflowArtifact),
  stageRuns: (response.stage_runs ?? response.stageRuns ?? []).map(mapStageRun),
  designChanges: (response.design_changes ?? response.designChanges ?? []).map(mapSessionDesignChange),
  failureModes: (response.failure_modes ?? response.failureModes ?? []).map(mapFailureMode),
  countermeasures: (response.countermeasures ?? []).map(mapCountermeasure),
  decisionCards: response.decision_cards ?? response.decisionCards ?? [],
  readiness: mapReadiness(response.readiness ?? {}),
  functionPartMatrix: mapFunctionPartMatrix(
    response.function_part_matrix ?? response.functionPartMatrix,
  ),
});

export const mapFunctionPartMatrix = (value: any): FunctionPartMatrix | null => {
  if (!value) return null;
  return {
    requirements: (value.requirements ?? []).map((item: any) => ({
      id: item.id,
      requirementNo: item.requirement_no ?? item.requirementNo,
      viewpointCode: item.viewpoint_code ?? item.viewpointCode,
      functionL1: item.function_l1 ?? item.functionL1 ?? '',
      functionL2: item.function_l2 ?? item.functionL2 ?? '',
      functionL3: item.function_l3 ?? item.functionL3 ?? '',
      requirementText: item.requirement_text ?? item.requirementText,
      inspectionAxisHints: item.inspection_axis_hints ?? item.inspectionAxisHints ?? [],
      sortOrder: item.sort_order ?? item.sortOrder ?? 0,
    })),
    partNodes: (value.part_nodes ?? value.partNodes ?? []).map((item: any) => ({
      id: item.id,
      partNo: item.part_no ?? item.partNo,
      parentPartNo: item.parent_part_no ?? item.parentPartNo ?? null,
      unitName: item.unit_name ?? item.unitName ?? '',
      partName: item.part_name ?? item.partName,
      role: item.role ?? '',
      sortOrder: item.sort_order ?? item.sortOrder ?? 0,
    })),
    axes: (value.axes ?? []).map((item: any) => ({
      id: item.id,
      axisCode: item.axis_code ?? item.axisCode,
      axisLabel: item.axis_label ?? item.axisLabel,
      category: item.category,
      description: item.description ?? '',
      sortOrder: item.sort_order ?? item.sortOrder ?? 0,
    })),
    cells: (value.cells ?? []).map((item: any) => ({
      id: item.id,
      partNodeId: item.part_node_id ?? item.partNodeId,
      axisId: item.axis_id ?? item.axisId,
      status: item.status,
      oldValue: item.old_value ?? item.oldValue ?? '',
      newValue: item.new_value ?? item.newValue ?? '',
      changeSummary: item.change_summary ?? item.changeSummary ?? '',
      affectedRequirementIds:
        item.affected_requirement_ids ?? item.affectedRequirementIds ?? [],
      aiReason: item.ai_reason ?? item.aiReason ?? '',
      aiSuggestedStatus:
        item.source_evidence?.ai_suggested_status ?? item.aiSuggestedStatus,
      reviewerNote: item.reviewer_note ?? item.reviewerNote ?? '',
      confidence: item.confidence ?? null,
      reviewedBy: item.reviewed_by ?? item.reviewedBy ?? null,
      reviewedAt: item.reviewed_at ?? item.reviewedAt ?? null,
      promotedDesignChangeId:
        item.promoted_design_change_id ?? item.promotedDesignChangeId ?? null,
    })),
    coverage: {
      totalCells: value.coverage?.total_cells ?? value.coverage?.totalCells ?? 0,
      uncheckedCells:
        value.coverage?.unchecked_cells ?? value.coverage?.uncheckedCells ?? 0,
      noneCells: value.coverage?.none_cells ?? value.coverage?.noneCells ?? 0,
      changedCells:
        value.coverage?.changed_cells ?? value.coverage?.changedCells ?? 0,
      importantChangedCells:
        value.coverage?.important_changed_cells
        ?? value.coverage?.importantChangedCells
        ?? 0,
      importantWithoutRequirements:
        value.coverage?.important_without_requirements
        ?? value.coverage?.importantWithoutRequirements
        ?? 0,
      unknownCells:
        value.coverage?.unknown_cells ?? value.coverage?.unknownCells ?? 0,
      notApplicableCells:
        value.coverage?.not_applicable_cells
        ?? value.coverage?.notApplicableCells
        ?? 0,
      reviewComplete:
        value.coverage?.review_complete ?? value.coverage?.reviewComplete ?? false,
      promotionReady:
        value.coverage?.promotion_ready ?? value.coverage?.promotionReady ?? false,
    },
    sourceSheets: (value.source_sheets ?? value.sourceSheets ?? []).map((item: any) => ({
      name: item.name,
      maxRow: item.max_row ?? item.maxRow,
      maxColumn: item.max_column ?? item.maxColumn,
    })),
  };
};

export function mapReadiness(value: any): DRBFMReadiness {
  return {
    readiness: value.readiness ?? 'draft',
    allowedExportModes: value.allowed_export_modes ?? value.allowedExportModes ?? ['draft'],
    blockers: (value.blockers ?? []).map((blocker: any) => ({
      type: blocker.type,
      artifactId: blocker.artifact_id ?? blocker.artifactId,
      state: blocker.state,
    })),
    blockerCounts: value.blocker_counts ?? value.blockerCounts ?? {},
    counts: value.counts ?? {},
  };
}

export const mapFailureMode = (fm: any): FailureMode => ({
  id: fm.id,
  sessionId: fm.session_id ?? fm.sessionId,
  designChangeId: fm.design_change_id ?? fm.designChangeId,
  failureDescription: fm.failure_description ?? fm.failureDescription,
  failureCause: fm.failure_cause ?? fm.failureCause,
  failureEffect: fm.failure_effect ?? fm.failureEffect,
  severity: fm.severity,
  severityScore: fm.severity_score ?? fm.severityScore,
  rpnScore: fm.rpn_score ?? fm.rpnScore,
  severityCandidateScore: fm.severity_candidate_score ?? fm.severityCandidateScore,
  occurrenceScore: fm.occurrence_score ?? fm.occurrenceScore,
  detectionScore: fm.detection_score ?? fm.detectionScore,
  scoreStatus: fm.score_status ?? fm.scoreStatus ?? 'unscored',
  reviewStatus: fm.review_status ?? fm.reviewStatus ?? 'ai_candidate',
  countermeasureRequirement: fm.countermeasure_requirement
    ?? fm.countermeasureRequirement ?? 'pending',
  countermeasureNotRequiredReason: fm.countermeasure_not_required_reason
    ?? fm.countermeasureNotRequiredReason,
  causalClaims: (fm.causal_claims ?? fm.causalClaims ?? []).map((claim: any) => ({
    claim: claim.claim,
    evidenceLevel: claim.evidence_level ?? claim.evidenceLevel ?? 'assumption',
    evidence: claim.evidence,
  })),
  knowledgeReferences: (fm.knowledge_references ?? fm.knowledgeReferences ?? [])
    .map(mapKnowledgeReference),
  drawingEvidence: mapDrawingEvidence(fm.drawing_evidence ?? fm.drawingEvidence),
  reviewQuestions: mapReviewQuestions(fm.review_questions ?? fm.reviewQuestions),
  usedPrecheckAnswers: mapUsedPrecheckAnswers(
    fm.used_precheck_answers,
    fm.usedPrecheckAnswers,
  ),
  unknownAssumptions: mapUnknownAssumptions(
    fm.unknown_assumptions,
    fm.unknownAssumptions,
  ),
  reviewerNote: fm.reviewer_note ?? fm.reviewerNote,
  reviewedBy: fm.reviewed_by ?? fm.reviewedBy,
  reviewedAt: fm.reviewed_at ?? fm.reviewedAt,
  supersededBy: fm.superseded_by ?? fm.supersededBy,
  cadAnchor: (fm.cad_anchor ?? fm.cadAnchor)
    ? {
      cadModelId: fm.cad_anchor?.cad_model_id ?? fm.cadAnchor?.cadModelId,
      partLabel: fm.cad_anchor?.part_label ?? fm.cadAnchor?.partLabel,
      position: (fm.cad_anchor?.position ?? fm.cadAnchor?.position) as [number, number, number],
      confidence: fm.cad_anchor?.confidence ?? fm.cadAnchor?.confidence,
      source: fm.cad_anchor?.source ?? fm.cadAnchor?.source,
      reasoning: fm.cad_anchor?.reasoning ?? fm.cadAnchor?.reasoning,
    }
    : undefined,
  createdAt: fm.created_at ?? fm.createdAt,
});

export const mapCountermeasure = (cm: any): Countermeasure => ({
  id: cm.id,
  failureModeId: cm.failure_mode_id ?? cm.failureModeId,
  sessionId: cm.session_id ?? cm.sessionId,
  countermeasureType: cm.countermeasure_type ?? cm.countermeasureType,
  description: cm.description,
  effectivenessScore: cm.effectiveness_score ?? cm.effectivenessScore,
  priority: cm.priority,
  reviewStatus: cm.review_status ?? cm.reviewStatus ?? 'ai_candidate',
  targetCause: cm.target_cause ?? cm.targetCause,
  mechanism: cm.mechanism,
  verificationMethod: cm.verification_method ?? cm.verificationMethod,
  acceptanceCriteria: cm.acceptance_criteria ?? cm.acceptanceCriteria,
  prerequisites: cm.prerequisites ?? [],
  unknownAssumptions: cm.unknown_assumptions ?? cm.unknownAssumptions ?? [],
  validatedEffectivenessScore: cm.validated_effectiveness_score
    ?? cm.validatedEffectivenessScore,
  feasibilityScore: cm.feasibility_score ?? cm.feasibilityScore,
  priorityClass: cm.priority_class ?? cm.priorityClass,
  priorityBasis: cm.priority_basis ?? cm.priorityBasis,
  reviewerNote: cm.reviewer_note ?? cm.reviewerNote,
  reviewedBy: cm.reviewed_by ?? cm.reviewedBy,
  reviewedAt: cm.reviewed_at ?? cm.reviewedAt,
  supersededBy: cm.superseded_by ?? cm.supersededBy,
  createdAt: cm.created_at ?? cm.createdAt,
});

export const mapSessionResults = (response: any): SessionResultsResponse => {
  if (!response.session) {
    throw new Error('DRBFM results response did not include a session.');
  }

  return {
    session: mapSession(response.session),
    designChanges: (response.design_changes ?? response.designChanges ?? []).map(mapSessionDesignChange),
    failureModes: (response.failure_modes ?? response.failureModes ?? []).map(mapFailureMode),
    countermeasures: (response.countermeasures ?? []).map(mapCountermeasure),
  };
};

export const mapCaseDesignChange = (dc: any): DRBFMCaseDesignChange => {
  const source = dc.source && typeof dc.source === 'object' && !Array.isArray(dc.source)
    ? dc.source
    : {};

  return {
    id: dc.id,
    caseId: dc.case_id ?? dc.caseId,
    category: dc.category,
    itemName: dc.item_name ?? dc.itemName,
    oldSpecification: dc.old_specification ?? dc.oldSpecification ?? '',
    newSpecification: dc.new_specification ?? dc.newSpecification ?? '',
    changeDescription: dc.change_description ?? dc.changeDescription ?? '',
    impactAreas: dc.impact_areas ?? dc.impactAreas ?? [],
    confidence: dc.confidence ?? null,
    source,
    sortOrder: dc.sort_order ?? dc.sortOrder ?? 0,
    createdAt: dc.created_at ?? dc.createdAt,
  };
};

export const mapCasePrecheckItem = (item: any): DRBFMCasePrecheckItem => ({
  id: item.id,
  caseId: item.case_id ?? item.caseId,
  question: item.question,
  reason: item.reason ?? '',
  requiredContextKey: item.required_context_key ?? item.requiredContextKey,
  answerStatus: item.answer_status ?? item.answerStatus ?? 'unanswered',
  answerText: item.answer_text ?? item.answerText ?? null,
  sortOrder: item.sort_order ?? item.sortOrder ?? 0,
  createdAt: item.created_at ?? item.createdAt,
  updatedAt: item.updated_at ?? item.updatedAt,
});

export const mapCase = (value: any): DRBFMCase => ({
  id: value.id,
  tenantId: value.tenant_id ?? value.tenantId,
  userId: value.user_id ?? value.userId,
  linkedSessionId: value.linked_session_id ?? value.linkedSessionId ?? null,
  title: value.title,
  productName: value.product_name ?? value.productName,
  targetPart: value.target_part ?? value.targetPart,
  changeSummary: value.change_summary ?? value.changeSummary,
  sourceDocumentIds: value.source_document_ids ?? value.sourceDocumentIds ?? [],
  context: value.context ?? {},
  status: value.status,
  designChanges: (value.design_changes ?? value.designChanges ?? []).map(mapCaseDesignChange),
  precheckItems: (value.precheck_items ?? value.precheckItems ?? []).map(mapCasePrecheckItem),
  createdAt: value.created_at ?? value.createdAt,
  updatedAt: value.updated_at ?? value.updatedAt,
  completedAt: value.completed_at ?? value.completedAt ?? null,
});

export const caseDesignChangeToRequest = (change: DRBFMCaseDesignChange) => ({
  id: change.id || undefined,
  category: change.category,
  item_name: change.itemName,
  old_specification: change.oldSpecification,
  new_specification: change.newSpecification,
  change_description: change.changeDescription,
  impact_areas: change.impactAreas || [],
  confidence: change.confidence ?? null,
  source: change.source || {},
  sort_order: change.sortOrder || undefined,
});

export const precheckAnswerToRequest = (item: DRBFMCasePrecheckItem) => ({
  id: item.id,
  answer_status: item.answerStatus,
  answer_text: item.answerText ?? null,
});

export const mapWorkflowOptions = (
  options: Record<string, unknown> = {},
): Record<string, unknown> => {
  const {
    inputPattern,
    changeGranularity,
    designChangeIds,
    failureModeIds,
    ...rest
  } = options;
  const mapped: Record<string, unknown> = { ...rest };
  const inputPatternValue = inputPattern ?? options.input_pattern;
  const granularityValue = changeGranularity ?? options.change_granularity;
  if (inputPatternValue !== undefined) mapped.input_pattern = inputPatternValue;
  if (granularityValue !== undefined) mapped.change_granularity = granularityValue;
  if (designChangeIds !== undefined) mapped.design_change_ids = designChangeIds;
  if (failureModeIds !== undefined) mapped.failure_mode_ids = failureModeIds;
  return mapped;
};

/**
 * Sanitize filename for safe download
 * Ensures .html extension and handles special characters
 */
export const sanitizeFilename = (name: string): string => {
  // Ensure the filename has .html extension
  let safeName = name;
  if (!safeName.toLowerCase().endsWith('.html')) {
    safeName = `${safeName}.html`;
  }

  // Replace characters that are problematic in filenames
  safeName = safeName.replace(/[<>:"/\\|?*]/g, '_');

  return safeName;
};
